import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { stickerTextStyle } from './StickerText';
import { SparklesIcon, SearchIcon, CloseIcon } from './Icons';
import api from '../api';

export interface GifItem {
    id: string;
    url: string;
    name: string;
    source: 'tenor' | 'giphy';
}

export interface GifPickerModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSelectGif: (url: string, item?: GifItem) => void;
    title?: string;
    zIndexClass?: string;
}

export const GifPickerModal: React.FC<GifPickerModalProps> = ({
    isOpen,
    onClose,
    onSelectGif,
    title = 'Search GIFs',
    zIndexClass = 'z-[150]',
}) => {
    const { currentUser, usersData } = useAuth();
    const currentUserData = usersData ? usersData[currentUser] : undefined;
    const userThemeColor = currentUserData?.appThemeColor || '#3b82f6';
    const userTextColor = currentUserData?.appTextColor || '#ffffff';
    const userOpacity = (currentUserData?.postBlurOpacity !== undefined ? currentUserData.postBlurOpacity : 50) / 100;

    const getExactModalBg = (color: string, opacityVal: number) => {
        if (!color) return `rgba(26, 26, 46, ${opacityVal})`;
        if (color.startsWith('rgba')) return color.replace(/[\d.]+\)$/, `${opacityVal})`);
        if (color.startsWith('rgb')) return color.replace('rgb', 'rgba').replace(')', `, ${opacityVal})`);
        let hex = color.replace('#', '');
        if (hex.length === 3 || hex.length === 4) hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        return (!isNaN(r) && !isNaN(g) && !isNaN(b)) ? `rgba(${r}, ${g}, ${b}, ${opacityVal})` : `rgba(26, 26, 46, ${opacityVal})`;
    };

    const modalBg = getExactModalBg(userThemeColor, userOpacity);

    const [searchQuery, setSearchQuery] = useState('');
    const [results, setResults] = useState<GifItem[]>([]);
    const [loading, setLoading] = useState(false);

    const searchTenor = async (query: string): Promise<GifItem[]> => {
        try {
            const response = await api.get(`tenor/search?q=${encodeURIComponent(query)}`);
            const data = response.data;
            if (!data?.results) return [];
            return data.results.map((r: any) => ({
                id: `tenor-${r.id}`,
                url: r.media?.[0]?.gif?.url || r.url || '',
                name: r.title || 'Tenor GIF',
                source: 'tenor' as const,
            })).filter((item: GifItem) => Boolean(item.url));
        } catch (e) {
            console.error('Tenor search error:', e);
            return [];
        }
    };

    const searchGiphy = async (query: string): Promise<GifItem[]> => {
        try {
            const GIPHY_KEY = 'dc6zaTOxFJmzC';
            const endpoint = query.trim()
                ? `https://api.giphy.com/v1/gifs/search?api_key=${GIPHY_KEY}&q=${encodeURIComponent(query)}&limit=20&rating=g`
                : `https://api.giphy.com/v1/gifs/trending?api_key=${GIPHY_KEY}&limit=20&rating=g`;
            const response = await fetch(endpoint);
            const data = await response.json();
            return (data.data || []).map((r: any) => ({
                id: `giphy-${r.id}`,
                url: r.images?.fixed_height?.url || r.images?.original?.url || '',
                name: r.title || 'Giphy GIF',
                source: 'giphy' as const,
            })).filter((item: GifItem) => Boolean(item.url));
        } catch (e) {
            console.error('Giphy search error:', e);
            return [];
        }
    };

    const executeSearch = useCallback(async (query: string) => {
        setLoading(true);
        try {
            const [tenorRes, giphyRes] = await Promise.allSettled([
                searchTenor(query),
                searchGiphy(query),
            ]);

            const tenorList = tenorRes.status === 'fulfilled' ? tenorRes.value : [];
            const giphyList = giphyRes.status === 'fulfilled' ? giphyRes.value : [];

            // Interleave results cleanly
            const maxLength = Math.max(tenorList.length, giphyList.length);
            const interleaved: GifItem[] = [];
            for (let i = 0; i < maxLength; i++) {
                if (i < tenorList.length) interleaved.push(tenorList[i]);
                if (i < giphyList.length) interleaved.push(giphyList[i]);
            }
            setResults(interleaved);
        } catch (err) {
            console.error('GIF search error:', err);
            setResults([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!isOpen) return;
        const timer = setTimeout(() => {
            executeSearch(searchQuery);
        }, 200);
        return () => clearTimeout(timer);
    }, [isOpen, searchQuery, executeSearch]);

    if (!isOpen) return null;

    return (
        <div
            className={`fixed inset-0 bg-black/80 backdrop-blur-lg flex items-center justify-center p-4 animate-fade-in ${zIndexClass}`}
            onClick={onClose}
        >
            <div
                className="bg-[#0e0e11] border border-white/10 rounded-[2.5rem] w-full max-w-md h-[80vh] overflow-hidden flex flex-col shadow-2xl backdrop-blur-md"
                style={{ backgroundColor: modalBg, color: userTextColor }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="p-5 flex items-center justify-between border-b border-white/5">
                    <div className="flex items-center gap-2">
                        <SparklesIcon className="w-5 h-5 animate-pulse" style={{ color: userThemeColor }} />
                        <h3
                            className="text-sm font-black uppercase tracking-tight"
                            style={{ color: userThemeColor, ...stickerTextStyle(0.15) }}
                        >
                            {title}
                        </h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1.5 hover:bg-white/10 rounded-full transition-colors"
                        style={{ color: userTextColor }}
                    >
                        <CloseIcon className="w-4 h-4" />
                    </button>
                </div>

                {/* Controls section (Search input) */}
                <div className="p-4 border-b border-white/5 bg-black/20 border-white/10">
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Search GIFs..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full bg-black/30 border border-white/10 focus:border-white/30 rounded-2xl py-3 pl-10 pr-4 text-xs outline-none shadow-inner"
                            style={{ color: userTextColor }}
                        />
                        <SearchIcon
                            className="w-4 h-4 text-white/40 absolute left-3.5 top-1/2 -translate-y-1/2"
                            style={{ color: userThemeColor }}
                        />
                    </div>
                </div>

                {/* Results grid */}
                <div className="flex-1 overflow-y-auto p-4 allow-scroll">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center h-full py-12">
                            <div
                                className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin mb-3"
                                style={{ borderColor: userThemeColor, borderTopColor: 'transparent' }}
                            />
                            <span
                                className="text-[10px] font-black uppercase tracking-wider"
                                style={{ color: userThemeColor, ...stickerTextStyle(0.18) }}
                            >
                                Searching GIFs...
                            </span>
                        </div>
                    ) : results.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full py-12 text-center">
                            <span className="text-[10px] uppercase tracking-widest opacity-40" style={stickerTextStyle(0.18)}>
                                {searchQuery.trim() ? 'No GIFs found' : 'Type to search GIFs'}
                            </span>
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 gap-2.5">
                            {results.map((item) => (
                                <div
                                    key={item.id}
                                    onClick={() => {
                                        onSelectGif(item.url, item);
                                        onClose();
                                    }}
                                    className="relative aspect-video bg-black/40 rounded-xl overflow-hidden cursor-pointer border border-white/5 hover:border-white/30 transition-all transform active:scale-95 group shadow-md"
                                >
                                    <img
                                        src={item.url}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                        alt={item.name}
                                        loading="lazy"
                                    />
                                    {/* Source badge indicator */}
                                    <div className="absolute top-1.5 left-1.5 z-10 pointer-events-none">
                                        <span
                                            className={`text-[9px] font-black uppercase px-2 py-0.5 rounded-full shadow-md backdrop-blur-md border ${
                                                item.source === 'tenor'
                                                    ? 'bg-blue-600/80 text-blue-100 border-blue-400/40'
                                                    : 'bg-pink-600/80 text-pink-100 border-pink-400/40'
                                            }`}
                                            style={stickerTextStyle(0.12)}
                                        >
                                            {item.source}
                                        </span>
                                    </div>
                                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-2 pointer-events-none">
                                        <span className="text-[9px] font-bold text-white truncate w-full" style={stickerTextStyle(0.15)}>
                                            {item.name}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default GifPickerModal;
